import {makeStyles, Typography} from "@material-ui/core";
import PropTypes from "prop-types";
import React, {useRef, useState} from "react";
import Box from "@material-ui/core/Box";
import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import clsx from "clsx";
import CloseIcon from "../../widget/streamline-icon/CloseIcon";
import StreamlineIcon from "../streamline-icon/StreamlineIcon";
import InformationCircle from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/alerts/information-circle.svg";

const useStyles = makeStyles((theme) => ({
    popoverContainer: {
        display: "inline-flex",
        flexDirection: "row"
    },
    popoverIcon: {
        "& > i": {
            width: theme.spacing(3),
            height: theme.spacing(3),
            marginTop: theme.spacing(-0.5),
            color: theme.palette.text.primary
        },
        "&.error > i": {
            color: theme.palette.error.main,
        },
        "&.warning > i": {
            color: theme.palette.secondary["A700"],
        },
        "&.info > i": {
            color: theme.palette.secondary["700"],
        },
        "&.success > i": {
            color: theme.palette.primary["700"],
        },
        "&.start > i": {
            marginRight: theme.spacing(2),
        },
        "&.end": {
            order: 1,
            "& > i": {
                marginLeft: theme.spacing(2)
            }
        }
    },
    clickPointer: {
        "&:hover": {
            cursor: "pointer"
        }
    },
    textUnderline: {
        textDecoration: "underline"
    },
    popoverHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingTop: theme.spacing(0.5),
        paddingRight: theme.spacing(1.5),
        paddingLeft: theme.spacing(1.5),
        "& button": {
            marginLeft: theme.spacing(2)
        }
    }
}));


const CustomPopover = ({
    children,
    icon,
    iconSeverity,
    iconPosition,
    iconClassName,
    label,
    labelVariant,
    title,
    titleVariant,
    anchorOriginVertical,
    anchorOriginHorizontal,
    transformOriginVertical,
    transformOriginHorizontal,
    labelUnderline
}) => {
    const classes = useStyles();

    const [openPopover, setOpenPopover] = useState(false);
    const popoverIconRef = useRef(null);
    const popoverLabelRef = useRef(null);

    const handlePopoverOpen = () => {
        setOpenPopover(true);
    };

    const handlePopoverClose = () => {
        setOpenPopover(false);
    };

    return (

        <div className={classes.popoverContainer}>
            {icon &&
                <div
                    ref={popoverIconRef}
                    className={clsx(classes.popoverIcon, classes.clickPointer, iconSeverity, iconPosition, iconClassName)}
                >
                    <StreamlineIcon
                        alt="Info"
                        onClick={handlePopoverOpen}
                    >
                        {icon}
                    </StreamlineIcon>
                </div>
            }

            {label && (
                <Typography
                    variant={labelVariant}
                    className={clsx(classes.clickPointer,
                        {[classes.textUnderline]: labelUnderline})}
                    onClick={handlePopoverOpen}
                    ref={popoverLabelRef}
                >
                    {label}
                </Typography>
            )}

            {children && (
                <Popover
                    onClose={handlePopoverClose}
                    open={openPopover}
                    anchorOrigin={{
                        vertical: anchorOriginVertical,
                        horizontal: anchorOriginHorizontal,
                    }}
                    transformOrigin={{
                        vertical: transformOriginVertical,
                        horizontal: transformOriginHorizontal,
                    }}
                    anchorEl={popoverIconRef.current || popoverLabelRef.current}
                >
                    {title && (
                        <Box
                            className={classes.popoverHeader}
                        >
                            <Typography
                                variant={titleVariant}
                            >
                                {title}
                            </Typography>
                            <IconButton
                                aria-label="close"
                                onClick={handlePopoverClose}
                            >
                                <CloseIcon/>
                            </IconButton>
                        </Box>
                    )}
                    {children}
                </Popover>
            )}
        </div>
    );
};

CustomPopover.defaultProps = {
    icon: <InformationCircle/>,
    iconPosition: "start",
    labelVariant: "subtitle2",
    labelUnderline: true,
    titleVariant: "subtitle1",
    anchorOriginVertical: "center",
    anchorOriginHorizontal: "left",
    transformOriginVertical: "top",
    transformOriginHorizontal: "left",
};

CustomPopover.propTypes = {
    children: PropTypes.any.isRequired,
    icon: PropTypes.any,
    iconSeverity: PropTypes.string,
    iconPosition: PropTypes.string,
    iconClassName: PropTypes.string,
    label: PropTypes.string,
    labelVariant: PropTypes.string,
    title: PropTypes.string,
    titleVariant: PropTypes.string,
    anchorOriginVertical: PropTypes.string,
    anchorOriginHorizontal: PropTypes.string,
    transformOriginVertical: PropTypes.string,
    transformOriginHorizontal: PropTypes.string,
    labelUnderline: PropTypes.bool
};

export default CustomPopover;
